/** @jsxImportSource @emotion/react */
import React, { memo } from 'react';
import { MapContainer } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import './leaflet-map.css';
import styled from '@emotion/styled';
import { Z_INDEX } from '../../constants/zIndex';

import { DEFAULT_ZOOM, MAP_STYLE_URL, MAP_ATTRIBUTION } from './leaflet-map.constants';
import MapLibreTileLayer from './MapLibreTileLayer';
import UserLocation from './user-location';
import { useGeoLocation } from '../../hooks/useGeoLocation';
import { useLocationsData } from '../../hooks/useLocationsData';
import ChargerMarkerList from './ChargerMarkerList';
import LocationLegendBlock from '../location-legend-block/location-legend-block.component';
import SpinnerLoader from '../spinner-loader/spinner-loader.component';
import { useLeafletMap } from './MapContext';
import { ZoomControls } from './ZoomControls';
import { useMatch } from 'react-router-dom';

const DEFAULT_LOCATION = {
    lat: 43.84782251165933,
    lng: -79.38982117301637,
};

function LeafletMap() {
    const { userLocation, loading: isUserLocationLoading } = useGeoLocation();
    const match = useMatch('/locations/:locationId');
    const locationId = match?.params?.locationId;
    const { mappedLocations, isLocationsLoading, isLocationsReloading } =
        useLocationsData(locationId);
    const { setMap, map } = useLeafletMap();

    const isLoading =
        isUserLocationLoading || (isLocationsLoading && !isLocationsReloading) || !map;

    return (
        <>
            {isLoading ? (
                <SpinnerLoaderWrapper>
                    <SpinnerLoader variant="secondary" />
                </SpinnerLoaderWrapper>
            ) : null}
            <Container isLoading={isLoading}>
                <MapContainer
                    ref={setMap}
                    zoomControl={false}
                    center={DEFAULT_LOCATION}
                    zoom={DEFAULT_ZOOM}>
                    <MapLibreTileLayer
                        styleUrl={MAP_STYLE_URL}
                        attribution={MAP_ATTRIBUTION}
                    />
                    <UserLocation location={userLocation} />
                    <ChargerMarkerList locations={mappedLocations} />
                    <ZoomControls />
                </MapContainer>
                <LocationLegendBlock />
            </Container>
        </>
    );
}

const Container = styled.div<{ isLoading: boolean }>(props => ({
    filter: props.isLoading ? 'blur(50px)' : 'none',
}));

const SpinnerLoaderWrapper = styled.div({
    position: 'absolute',
    left: '50%',
    transform: ' translate(-50%, -50%)',
    zIndex: Z_INDEX.LEAFLET_LOADER,
    top: '50%',
});

export default memo(LeafletMap);
